import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaGasPump,
  FaCar,
  FaCog,
  FaInfoCircle,
  FaBuilding,
  FaCalendarAlt,
} from "react-icons/fa";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

/* =========================================================
   API URLS
========================================================= */

const API_BASE =
  "https://nissi-india.com/api/hsdhr";

/* =========================================================
   HELPERS
========================================================= */

const formatDate = (dateStr) => {
  if (!dateStr) {
    return "";
  }

  const [
    year,
    month,
    day,
  ] = dateStr.split("-");

  if (
    !year ||
    !month ||
    !day
  ) {
    return "";
  }

  return `${day}/${month}/${year}`;
};

const showValue = (value) =>
  value === null ||
  value === undefined ||
  value === ""
    ? "—"
    : value;

const toStrSafe = (value) =>
  String(value ?? "").trim();

/* =========================================================
   MONTH OPTIONS
========================================================= */

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* =========================================================
   COMPONENT
========================================================= */

const VehicleHsdFillingInsideReport = () => {
  const navigate =
    useNavigate();

  // Row + filters passed from Vehicle HSD Filling Report
  const location = useLocation();
  const navRow = location.state?.row || null;
  const navFilters = location.state?.filters || {};

  const storedBranchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";

  const storedBranchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  // Tenant / financial-year DB resolution
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": localStorage.getItem("finYearId") || "",
      "x-finyear": localStorage.getItem("finYear") || "",
      "x-branchid": storedBranchId,
      "x-branch": storedBranchType,
      "x-userid": localStorage.getItem("userId") || "",
    }),
    [storedBranchId, storedBranchType]
  );

  /* =======================================================
     THEME
  ======================================================= */

  const {
    isDark,
    skin,
    setSkin,
    theme,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    useMemo(
      () =>
        getCurrentSkin(
          theme || skin
        ),
      [
        theme,
        skin,
      ]
    );

  const baseColor =
    currentSkin?.baseColor ||
    "blue";

  const baseShade =
    currentSkin?.baseShade ||
    "600";

  /* =======================================================
     DESIGN CLASSES
  ======================================================= */

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark
      ? "text-slate-300"
      : "text-slate-700"
  }`;

  const cardClass = `rounded-2xl shadow-xl border overflow-hidden ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
      : `bg-white ${
          currentSkin?.glass || ""
        } border-gray-200`
  }`;

  const neutralButtonClass = `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition ${
    isDark
      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
      : "border-slate-300 text-slate-700 hover:bg-slate-100"
  }`;

  const tableRowClass = `transition-colors ${
    isDark
      ? "bg-gray-800/20 hover:bg-gray-700/30"
      : "bg-white hover:bg-gray-50"
  }`;

  const tableCellClass = `px-4 py-3 text-sm whitespace-nowrap ${
    isDark
      ? "text-gray-300"
      : "text-gray-900"
  }`;

  /* =======================================================
     STATE
  ======================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

    // "HO" = Own, "Branch" = Hire (radio values kept as-is)
  const [branchType, setBranchType] = useState(
    navFilters.vehicleType === "H" ? "Branch" : "HO"
  );

  const defaultBranch =
    storedBranchType === "HO" ? "" : storedBranchId;

  const [fromDate, setFromDate] = useState(navFilters.fromDate || "");
  const [toDate, setToDate] = useState(navFilters.toDate || "");

  // purchaseOrderNo = Reg No, projectCode = Vehicle Code, status = Vehicle Name
  const [purchaseOrderNo, setPurchaseOrderNo] = useState("");
  const [projectCode, setProjectCode] = useState(
    toStrSafe(navRow?.fld_vehicle_code)
  );
  const [status, setStatus] = useState("");

  const [branchSelection, setBranchSelection] = useState(
    navFilters.branch ?? defaultBranch
  );

  const [month, setMonth] = useState(navFilters.month || "");
  const [year, setYear] = useState(navFilters.year || "");

  const [
    yearList,
    setYearList,
  ] = useState([]);

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    supplierList,
    setSupplierList,
  ] = useState([]);

  const [
    data,
    setData,
  ] = useState([]);

  const [
    loadingDropdowns,
    setLoadingDropdowns,
  ] = useState(false);

  const [
    loadingYears,
    setLoadingYears,
  ] = useState(false);

  const [
    loadingReport,
    setLoadingReport,
  ] = useState(false);

  /* =======================================================
     LOAD BRANCHES
  ======================================================= */

  const loadBranches =
    useCallback(
      async () => {
        setLoadingDropdowns(
          true
        );

        try {
          const response =
            await axios.get(
              `${API_BASE}/fillbranch_hsdRpt`,
              {
                params: {
                  branchid: storedBranchId,
                  branchtype: storedBranchType,
                },
                headers: commonHeaders,
              }
            );

          setBranchList(
            Array.isArray(
              response.data
                ?.branchList
            )
              ? response.data
                  .branchList
              : []
          );
        } catch (error) {
          console.error(
            "Dropdown fetch failed:",
            error
          );

          toast.error(
            "Failed to load dropdowns"
          );

          setBranchList([]);
        } finally {
          setLoadingDropdowns(
            false
          );
        }
           },
      [storedBranchId, storedBranchType, commonHeaders]
    );

  /* =======================================================
     LOAD YEARS
       ======================================================= */

  const loadYears =
    useCallback(
      async () => {
        setLoadingYears(
          true
        );

        try {
          const response =
                        await axios.get(
              `${API_BASE}/hsd_years`,
              { headers: commonHeaders }
            );

          setYearList(
            Array.isArray(
              response.data
            )
              ? response.data
              : []
          );
        } catch (error) {
          console.error(
            "Years fetch failed:",
            error
          );

          toast.error(
            "Failed to load years"
          );

          setYearList([]);
        } finally {
          setLoadingYears(
            false
          );
        }
      },
      []
    );

  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    loadYears();
  }, [
    loadYears,
  ]);

  /* =======================================================
     BUILD PARAMS

     The original code was hardcoded to:
     month = April
     year = 2022

     Here the selected values are used when available,
     otherwise the same original fallbacks are preserved.
  ======================================================= */

  const buildParams =
    () => ({
      // "" = All Branches (HO only)
      branch:
        branchSelection ||
        (storedBranchType === "HO" ? "%" : storedBranchId),

      vehicleCode: projectCode || "",
      vehicleName: status || "",
      vehicleRegNo: purchaseOrderNo || "",

      from: formatDate(fromDate) || "",
      to: formatDate(toDate) || "",

      // "" = not selected (backend decides the default)
      month: month || "",
      year: year || "",

      vehicleType: branchType === "Branch" ? "H" : "O",
    });

  /* =======================================================
     SEARCH
  ======================================================= */

  const handleSearch =
    async () => {
      if (
        fromDate &&
        toDate &&
        new Date(
          fromDate
        ).getTime() >
          new Date(
            toDate
          ).getTime()
      ) {
        toast.warning(
          "From Date should not be greater than To Date"
        );

        return;
      }

      if (!projectCode && !status && !purchaseOrderNo) {
        toast.warning(
          "Please enter Vehicle Code or Name or Reg No"
        );
        return;
      }

      const params =
        buildParams();

      setLoadingReport(
        true
      );

      try {
        const response =
          await axios.get(
            `${API_BASE}/hsd_fillingInside`,
            {
              params,
              headers: commonHeaders,
            }
          );

        const rows =
          Array.isArray(
            response.data
              ?.data
          )
            ? response.data
                .data
            : [];

        setData(
          rows
        );

        if (
          rows.length ===
          0
        ) {
          toast.info(
            "No vehicle HSD records found"
          );
        } else {
          toast.success(
            `Found ${rows.length} record(s)`
          );
        }
      } catch (error) {
        console.error(
          "HSD Filling Inside fetch failed:",
          error
        );

        toast.error(
          error?.response
            ?.data
            ?.message ||
            "Failed to fetch HSD filling report"
        );

        setData([]);
      } finally {
        setLoadingReport(
          false
        );
      }
    };

  // Opened from the main report → load that vehicle immediately
  useEffect(() => {
    if (navRow?.fld_vehicle_code) {
      handleSearch();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* =======================================================
     PRINT
  ======================================================= */

  const handlePrint =
    () => {
      const params =
        buildParams();

      const query =
        new URLSearchParams(
          params
        ).toString();

      window.open(
        `/inventory/Vehiclehsdfillinginside_AllPrint?${query}`,
        "_blank"
      );
    };

  /* =======================================================
     RESET FILTERS
  ======================================================= */

  const clearFilters =
    () => {
      setBranchType(
        "HO"
      );

      setFromDate(
        ""
      );

      setToDate(
        ""
      );

      setPurchaseOrderNo(
        ""
      );

      setProjectCode(
        ""
      );

      setStatus(
        ""
      );

            setBranchSelection(
        defaultBranch
      );

      setMonth(
        ""
      );

      setYear(
        ""
      );

      setData(
        []
      );

      toast.info(
        "Filters cleared"
      );
    };

  /* =======================================================
     TOTALS
  ======================================================= */

  const totals =
    useMemo(() => {
      return data.reduce(
        (
          accumulator,
          item
        ) => {
          accumulator.workingHr +=
            Number(
              item.fld_Workinghr ||
                0
            ) || 0;

          accumulator.workingKm +=
            Number(
              item.fld_Workingkm ||
                0
            ) || 0;

          accumulator.dieselHr +=
            Number(
              item.fld_diselconsumptionhr ||
                0
            ) || 0;

          accumulator.dieselKm +=
            Number(
              item.fld_diselconsumptionkm ||
                0
            ) || 0;

          return accumulator;
        },
        {
          workingHr: 0,
          workingKm: 0,
          dieselHr: 0,
          dieselKm: 0,
        }
      );
    }, [data]);

  const isLoading =
    loadingDropdowns ||
    loadingYears ||
    loadingReport;

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <LayoutWrapper>
      {/* ===================================================
          HEADER
      =================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            VEHICLE HSD FILLING
            HR/KM REPORT
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">
        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <div
          className={
            cardClass
          }
        >
          <div
            className={`px-6 py-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >
            {/* LEFT */}

            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FaGasPump className="w-5 h-5 text-white" />
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3
                    className={`text-lg font-bold ${
                      isDark
                        ? "text-white"
                        : "text-slate-900"
                    }`}
                  >
                    Vehicle HSD Filling
                    Report
                  </h3>

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      isDark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {
                      data.length
                    }{" "}
                    Records
                  </span>

                  {isLoading && (
                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                        isDark
                          ? "bg-gray-700 text-gray-300"
                          : `bg-${baseColor}-50 text-${baseColor}-700`
                      }`}
                    >
                      <FaCog className="animate-spin" />

                      Loading...
                    </span>
                  )}
                </div>

                <p
                  className={`text-xs mt-1 ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Review vehicle HSD
                  filling hours,
                  kilometres, working
                  values and diesel
                  consumption
                </p>
              </div>
            </div>

            {/* RIGHT */}

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    (
                      previous
                    ) =>
                      !previous
                  )
                }
                className={
                  neutralButtonClass
                }
              >
                <FaFilter />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate(-1)
                }
                className={
                  neutralButtonClass
                }
              >
                <FaArrowLeft />

                Back
              </button>
            </div>
          </div>
        </div>

        {/* =================================================
            FILTER CARD
        ================================================= */}

        {showFilters && (
          <div
            className={
              cardClass
            }
          >
            {/* HEADER */}

            <div
              className={`px-6 py-4 border-b ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                >
                  <FaFilter className="w-4 h-4 text-white" />
                </div>

                <div>
                  <h3
                    className={`text-lg font-bold ${
                      isDark
                        ? "text-white"
                        : "text-slate-900"
                    }`}
                  >
                    Filtration Area
                  </h3>

                  <p
                    className={`text-xs ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Filter by vehicle,
                    branch, dates,
                    month and year
                  </p>
                </div>
              </div>
            </div>

            {/* BODY */}

            <div className="p-6">
              {/* OWN / HIRE */}

              <div
                className={`rounded-xl border p-4 mb-6 ${
                  isDark
                    ? "bg-slate-800/40 border-slate-700"
                    : "bg-slate-50 border-slate-200"
                }`}
              >
                <label
                  className={
                    labelClass
                  }
                >
                  Vehicle Type
                </label>

                <div className="flex flex-wrap items-center gap-6 mt-3">
                  <label
                    className={`inline-flex items-center gap-2 cursor-pointer text-sm font-medium ${
                      isDark
                        ? "text-gray-200"
                        : "text-gray-700"
                    }`}
                  >
                    <input
                      type="radio"
                      name="branchtype"
                      value="HO"
                      checked={
                        branchType ===
                        "HO"
                      }
                      onChange={() =>
                        setBranchType(
                          "HO"
                        )
                      }
                      className={`accent-${baseColor}-600`}
                    />

                    <FaCar />

                    Own Vehicle
                  </label>

                  <label
                    className={`inline-flex items-center gap-2 cursor-pointer text-sm font-medium ${
                      isDark
                        ? "text-gray-200"
                        : "text-gray-700"
                    }`}
                  >
                    <input
                      type="radio"
                      name="branchtype"
                      value="Branch"
                      checked={
                        branchType ===
                        "Branch"
                      }
                      onChange={() =>
                        setBranchType(
                          "Branch"
                        )
                      }
                      className={`accent-${baseColor}-600`}
                    />

                    <FaCar />

                    Hire Vehicle
                  </label>
                </div>
              </div>

              {/* FILTER GRID */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {/* FROM DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(
                      event
                    ) =>
                      setFromDate(
                        event.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(
                      event
                    ) =>
                      setToDate(
                        event.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* REG NO */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle
                    Registration No
                  </label>

                  <input
                    type="text"
                    value={
                      purchaseOrderNo
                    }
                    onChange={(
                      event
                    ) =>
                      setPurchaseOrderNo(
                        event.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                    placeholder="Enter Registration No"
                  />
                </div>

                {/* VEHICLE CODE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Code
                  </label>

                  <input
                    type="text"
                    value={
                      projectCode
                    }
                    onChange={(
                      event
                    ) =>
                      setProjectCode(
                        event.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                    placeholder="Enter Vehicle Code"
                  />
                </div>

                {/* VEHICLE NAME */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Name
                  </label>

                  <input
                    type="text"
                    value={status}
                    onChange={(event) =>
                      setStatus(event.target.value)
                    }
                    className={inputClass}
                    placeholder="Enter Vehicle Name"
                  />
                </div>
                {/* BRANCH */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch
                  </label>

                  <select
                    value={
                      branchSelection
                    }
                    onChange={(
                      event
                    ) => {
                      const value =
                        event.target
                          .value;

                      setBranchSelection(
                        value
                      );
                    }}
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select
                    </option>

                    {branchList.map(
                      (
                        branchItem,
                        index
                      ) => (
                        <option
                          key={
                            branchItem.fld_Branchid ||
                            index
                          }
                          value={
                            branchItem.fld_Branchid
                          }
                        >
                          {
                            branchItem.fld_BranchName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* MONTH */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Month
                  </label>

                  <select
                    value={
                      month
                    }
                    onChange={(
                      event
                    ) =>
                      setMonth(
                        event.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select
                    </option>

                    {MONTH_OPTIONS.map(
                      (
                        monthName
                      ) => (
                        <option
                          key={
                            monthName
                          }
                          value={
                            monthName
                          }
                        >
                          {
                            monthName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* YEAR */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Year
                  </label>

                  <select
                    value={
                      year
                    }
                    onChange={(
                      event
                    ) =>
                      setYear(
                        event.target
                          .value
                      )
                    }
                    disabled={
                      loadingYears
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select
                    </option>

                    {yearList.map(
                      (
                        yearItem,
                        index
                      ) => (
                        <option
                          key={
                            yearItem.value ||
                            index
                          }
                          value={
                            yearItem.value
                          }
                        >
                          {
                            yearItem.text
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* ACTIONS */}

              <div
                className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6 pt-5 border-t ${
                  isDark
                    ? "border-slate-700"
                    : "border-slate-200"
                }`}
              >
                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={
                    neutralButtonClass
                  }
                >
                  Clear Filters
                </button>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={
                      handleSearch
                    }
                    disabled={
                      loadingReport
                    }
                    className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60`}
                  >
                    {loadingReport ? (
                      <FaCog className="animate-spin" />
                    ) : (
                      <FaSearch />
                    )}

                    {loadingReport
                      ? "Searching..."
                      : "Search Records"}
                  </button>

                  <button
                    type="button"
                    onClick={
                      handlePrint
                    }
                    className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-amber-500 hover:bg-amber-600"
                  >
                    <FaPrint />

                    Print Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* =================================================
            RESULT CARD
        ================================================= */}

        <div
          className={
            cardClass
          }
        >
          {/* HEADER */}

          <div
            className={`p-6 border-b ${
              isDark
                ? "border-gray-700"
                : "border-gray-200"
            }`}
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <div>
                  <h3
                    className={`text-xl font-bold ${
                      isDark
                        ? "text-white"
                        : "text-gray-900"
                    }`}
                  >
                    HSD Filling
                    HR/KM Records
                  </h3>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-gray-400"
                        : "text-gray-500"
                    }`}
                  >
                    Opening, closing,
                    working and diesel
                    consumption values
                  </p>
                </div>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {
                    data.length
                  }{" "}
                  records
                </span>
              </div>

              {data.length >
                0 && (
                <div className="flex flex-wrap gap-2">
                  <span
                    className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                      isDark
                        ? "bg-blue-900/30 text-blue-300"
                        : "bg-blue-50 text-blue-700"
                    }`}
                  >
                    Working Hr:{" "}
                    {
                      totals.workingHr
                    }
                  </span>

                  <span
                    className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                      isDark
                        ? "bg-emerald-900/30 text-emerald-300"
                        : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    Running KM:{" "}
                    {
                      totals.workingKm
                    }
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* LOADING */}

          {loadingReport ? (
            <div className="text-center py-16">
              <FaCog
                className={`w-10 h-10 mx-auto animate-spin text-${baseColor}-500`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-gray-400"
                    : "text-gray-500"
                }`}
              >
                Loading Vehicle HSD
                records...
              </p>
            </div>
          ) : data.length >
            0 ? (
            /* TABLE */

            <div className="overflow-x-auto">
              <table className="min-w-[1600px] w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                      Branch
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                      HSD Filling Opening Hr
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                      HSD Filling Closing Hr
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                      Working Hr
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                      HSD Filling Opening KM
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                      HSD Filling Closing KM
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                      Running KM
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                      HSD
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                      Diesel Consumption Hr
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                      Diesel Consumption KM
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-700"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={`${item.fld_Date || "row"}-${index}`}
                        className={
                          tableRowClass
                        }
                      >
                        {/* S.NO */}

                        <td
                          className={`${tableCellClass} text-center font-medium`}
                        >
                          {index +
                            1}
                        </td>

                        {/* DATE */}

                        <td
                          className={
                            tableCellClass
                          }
                        >
                          <div className="flex items-center gap-2">
                            <FaCalendarAlt
                              className={`text-${baseColor}-500`}
                            />

                            <span>
                              {showValue(
                                item.fld_Date
                              )}
                            </span>
                          </div>
                        </td>

                        {/* BRANCH */}

                        <td
                          className={
                            tableCellClass
                          }
                        >
                          <div className="flex items-center gap-2">
                            <FaBuilding
                              className={`text-${baseColor}-500`}
                            />

                            <span className="font-medium">
                              {showValue(
                                item.fld_BranchName
                              )}
                            </span>
                          </div>
                        </td>

                        {/* OPEN HR */}

                        <td
                          className={`${tableCellClass} text-right`}
                        >
                          {showValue(
                            item.fld_hsdfillingopeninghr
                          )}
                        </td>

                        {/* CLOSE HR */}

                        <td
                          className={`${tableCellClass} text-right`}
                        >
                          {showValue(
                            item.fld_hsdfillingclosinghr
                          )}
                        </td>

                        {/* WORKING HR */}

                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <span
                            className={`inline-flex min-w-[70px] justify-end px-3 py-1.5 rounded-lg font-semibold ${
                              isDark
                                ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                                : `bg-${baseColor}-50 text-${baseColor}-700`
                            }`}
                          >
                            {showValue(
                              item.fld_Workinghr
                            )}
                          </span>
                        </td>

                        {/* OPEN KM */}

                        <td
                          className={`${tableCellClass} text-right`}
                        >
                          {showValue(
                            item.fld_hsdfillingopeningkm
                          )}
                        </td>

                        {/* CLOSE KM */}

                        <td
                          className={`${tableCellClass} text-right`}
                        >
                          {showValue(
                            item.fld_hsdfillingclosingkm
                          )}
                        </td>

                        {/* RUNNING KM */}

                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <span
                            className={`inline-flex min-w-[70px] justify-end px-3 py-1.5 rounded-lg font-semibold ${
                              isDark
                                ? "bg-emerald-900/30 text-emerald-300"
                                : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {showValue(
                              item.fld_Workingkm
                            )}
                          </span>
                        </td>

                        {/* HSD */}

                        <td
                          className={`${tableCellClass} text-right`}
                        >
                          {showValue(
                            item.fld_Qty
                          )}
                        </td>

                        {/* DIESEL HR */}

                        <td
                          className={`${tableCellClass} text-right`}
                        >
                          {showValue(
                            item.fld_diselconsumptionhr
                          )}
                        </td>

                        {/* DIESEL KM */}

                        <td
                          className={`${tableCellClass} text-right`}
                        >
                          {showValue(
                            item.fld_diselconsumptionkm
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            /* EMPTY */

            <div className="text-center py-14">
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                  isDark
                    ? "bg-gray-800"
                    : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-8 h-8 text-gray-400" />
              </div>

              <h3
                className={`font-semibold ${
                  isDark
                    ? "text-gray-200"
                    : "text-gray-800"
                }`}
              >
                No Vehicle HSD
                Records Found
              </h3>

              <p
                className={`text-sm mt-1 ${
                  isDark
                    ? "text-gray-400"
                    : "text-gray-500"
                }`}
              >
                Select the required
                filters and click
                Search Records.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ===================================================
          TOAST
      =================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default VehicleHsdFillingInsideReport;